import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Lock, ShoppingCart, ArrowRight, Calendar } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { BuyCreditsModal } from './BuyCreditsModal';

/**
 * BILLING-02 — Full blocking screen rendered inside the consultation flow when
 * generate-documentation returns 403 "limite_atingido".
 *
 * Props:
 *   info: {
 *     consultations_used, consultations_included, credits_balance,
 *     plan_type, can_buy_credits, reset_date?
 *   }
 */
export const LimitReachedScreen = ({ info = {} }) => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [creditsOpen, setCreditsOpen] = useState(false);

  const canBuyCredits = info.can_buy_credits === true || info.plan_type === 'solo' || info.plan_type === 'clinica';
  const resetDate = info.reset_date ? new Date(info.reset_date) : null;
  const resetLabel = resetDate
    ? resetDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : null;

  return (
    <div
      className={`max-w-2xl mx-auto py-12 px-6 text-center rounded-2xl border ${
        isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'
      }`}
      data-testid="limit-reached-screen"
    >
      <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-5">
        <Lock className="h-8 w-8 text-red-600 dark:text-red-400" />
      </div>

      <h2 className={`text-2xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
        Limite de consultas atingido
      </h2>

      <p className={`text-base mb-6 ${isDark ? 'text-slate-300' : 'text-slate-600'}`} data-testid="limit-reached-body">
        Você usou todas as {info.consultations_included || 0} consultas incluídas no seu plano este mês.
      </p>

      {canBuyCredits ? (
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            size="lg"
            className="bg-primary hover:bg-primary-hover"
            onClick={() => setCreditsOpen(true)}
            data-testid="limit-buy-credits-btn"
          >
            <ShoppingCart className="h-4 w-4 mr-2" />
            Comprar mais consultas
          </Button>
          <Button
            size="lg"
            variant="outline"
            onClick={() => navigate('/billing')}
            data-testid="limit-see-plan-btn"
          >
            Ver meu plano
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            size="lg"
            className="bg-primary hover:bg-primary-hover"
            onClick={() => navigate('/upgrade?source=limit_reached')}
            data-testid="limit-upgrade-btn"
          >
            Fazer upgrade para Solo
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          {resetLabel && (
            <div
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-md ${
                isDark ? 'bg-slate-900 text-slate-400' : 'bg-slate-100 text-slate-600'
              }`}
              data-testid="limit-reset-info"
            >
              <Calendar className="h-4 w-4" />
              Aguardar renovação em {resetLabel}
            </div>
          )}
        </div>
      )}

      <BuyCreditsModal
        open={creditsOpen}
        onOpenChange={setCreditsOpen}
        canBuyCredits={canBuyCredits}
      />
    </div>
  );
};
